import { useState } from 'react';
import type { ComponentType, SVGProps } from 'react';
import { Link } from 'react-router-dom';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { IconBed, IconClock, IconDroplet, IconFlask, IconLock, IconPulse, IconUsers } from '../../components/layout/icons';
import { ErrorState, LoadingState } from '../../components/ui/Feedback';
import { ChartSkeleton, Sk } from '../../components/ui/Skeleton';
import { getSession } from '../../lib/auth';
import { formatDate, APP_TIMEZONE } from '../../lib/datetime';
import { usePatients, usePatientStats } from '../patients/patient.queries';
import type { ReportSection } from '../reports/types';
import { useDashboardCollections, useDashboardSummary } from './dashboard.queries';
import type { DashboardDepartmentKey } from './types';

const formatDateValue = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return formatDate(date);
};

const formatDayLabel = (isoDate: string, longRange = false) => new Intl.DateTimeFormat('en-IN', {
  day: longRange ? 'numeric' : undefined,
  month: longRange ? 'short' : undefined,
  weekday: longRange ? undefined : 'short',
  timeZone: APP_TIMEZONE,
}).format(new Date(`${isoDate}T00:00:00`));

type TrendDays = 7 | 14 | 30;
const TREND_DAY_OPTIONS: TrendDays[] = [7, 14, 30];

const currency = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 });

type DeptAccent = 'violet' | 'rose' | 'indigo' | 'amber' | 'teal' | 'cyan';

interface DepartmentDatum {
  key: DashboardDepartmentKey;
  collectionKey: ReportSection;
  label: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  accent: DeptAccent;
  today: number;
  yesterday: number;
  countLabel: string;
  live?: boolean;
  caption?: string;
}

const appDateKey = (date: Date) => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: APP_TIMEZONE,
  }).formatToParts(date);
  const value = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? '';
  return `${value('year')}-${value('month')}-${value('day')}`;
};

const greetingFor = (date: Date) => {
  const hour = Number(new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    hourCycle: 'h23',
    timeZone: APP_TIMEZONE,
  }).format(date));
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
};

const ChartTooltip = ({ active, payload, label, prefix }: { active?: boolean; payload?: { name: string; value: number; color: string }[]; label?: string; prefix?: string }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip">
      <strong>{label}</strong>
      {payload.map((entry) => (
        <div key={entry.name} className="chart-tooltip-row">
          <span className="chart-tooltip-dot" style={{ background: entry.color }} />
          <span>{entry.name}</span>
          <b>{prefix ? `${prefix}${entry.value.toLocaleString('en-IN')}` : entry.value.toLocaleString('en-IN')}</b>
        </div>
      ))}
    </div>
  );
};

const DepartmentCard = ({ dept }: { dept: DepartmentDatum }) => (
  <article className={`dept-card accent-${dept.accent}`}>
    <span className="dept-card-top" aria-hidden="true" />
    <div className="dept-card-head">
      <span className="dept-icon" aria-hidden="true"><dept.icon /></span>
      <span className="dept-label" title={dept.label}>{dept.label}</span>
      {dept.live ? <span className="status-dot" title="Live data" /> : <span className="dept-soon-badge" title={`${dept.label} module coming soon`}><IconLock /></span>}
    </div>
    <div className="dept-count-row">
      <strong>{dept.today.toLocaleString('en-IN')}</strong>
      <span>{dept.countLabel}</span>
    </div>
    <p className="dept-yesterday">
      {dept.caption ?? <>Yesterday <b>{dept.yesterday.toLocaleString('en-IN')}</b></>}
    </p>
  </article>
);

const DepartmentCardsSkeleton = () => (
  <section className="dept-grid skeleton-region" aria-busy="true" aria-label="Loading department summary">
    {Array.from({ length: 6 }, (_, index) => (
      <article className="dept-card" key={index} aria-hidden="true">
        <span className="dept-card-top" />
        <div className="dept-card-head"><Sk className="dept-sk-icon" /><Sk className="sk-label" style={{ width: '52%' }} /></div>
        <Sk style={{ height: 24, width: '36%', margin: '10px 0' }} />
        <Sk className="sk-label" style={{ width: '62%' }} />
      </article>
    ))}
  </section>
);

export const DashboardPage = () => {
  const [trendDays, setTrendDays] = useState<TrendDays>(30);
  const now = new Date();
  const todayKey = appDateKey(now);
  const yesterdayKey = appDateKey(new Date(now.getTime() - 86_400_000));
  const patientsQuery = usePatients(1, '', 5);
  const statsQuery = usePatientStats(trendDays);
  const dashboardQuery = useDashboardSummary();
  const collectionQuery = useDashboardCollections(yesterdayKey, todayKey);
  const recentPatients = patientsQuery.data?.data ?? [];
  const stats = statsQuery.data;
  const departmentSummary = dashboardQuery.data?.departments;
  const totalPatients = stats?.total ?? patientsQuery.data?.pagination.total ?? 0;
  const collectionToday = collectionQuery.data?.dateRows.find((row) => row.date === todayKey);
  const collectionYesterday = collectionQuery.data?.dateRows.find((row) => row.date === yesterdayKey);
  const displayName = getSession()?.user.name.trim() || 'Admin';

  const currentDate = formatDate(now);
  const greeting = greetingFor(now);

  const departments: DepartmentDatum[] = [
    {
      key: 'opd',
      collectionKey: 'opd',
      label: 'OPD',
      icon: IconUsers,
      accent: 'violet',
      today: departmentSummary?.opd.today ?? 0,
      yesterday: departmentSummary?.opd.yesterday ?? 0,
      countLabel: 'registrations today',
      live: true,
    },
    { key: 'emergency', collectionKey: 'emg', label: 'Emergency', icon: IconPulse, accent: 'rose', today: departmentSummary?.emergency.today ?? 0, yesterday: departmentSummary?.emergency.yesterday ?? 0, countLabel: 'registrations today', live: true },
    { key: 'ipd', collectionKey: 'ipd', label: 'IPD', icon: IconBed, accent: 'indigo', today: departmentSummary?.ipd.today ?? 0, yesterday: departmentSummary?.ipd.yesterday ?? 0, countLabel: 'admissions today', live: true },
    { key: 'daycare', collectionKey: 'daycare', label: 'Daycare', icon: IconClock, accent: 'amber', today: departmentSummary?.daycare.today ?? 0, yesterday: departmentSummary?.daycare.yesterday ?? 0, countLabel: 'admissions today', live: true },
    { key: 'dialysis', collectionKey: 'dialysis', label: 'Dialysis', icon: IconDroplet, accent: 'teal', today: departmentSummary?.dialysis.today ?? 0, yesterday: departmentSummary?.dialysis.yesterday ?? 0, countLabel: 'sessions today', live: true },
    { key: 'investigation', collectionKey: 'investigation', label: 'Investigation', icon: IconFlask, accent: 'cyan', today: departmentSummary?.investigation.today ?? 0, yesterday: departmentSummary?.investigation.yesterday ?? 0, countLabel: 'tests today', live: true },
  ];

  const patientChartData = departments.map((dept) => ({ department: dept.label, Yesterday: dept.yesterday, Today: dept.today }));
  const collectionChartData = departments.map((dept) => ({
    department: dept.label,
    Yesterday: collectionYesterday?.sections[dept.collectionKey].total ?? 0,
    Today: collectionToday?.sections[dept.collectionKey].total ?? 0,
  }));
  const trendData = (stats?.trend ?? []).map((point) => ({
    day: formatDayLabel(point.date, trendDays > 7),
    Registrations: point.count,
  }));

  return (
    <div className="page-stack">
      <div className="page-heading">
        <div><p className="eyebrow">{currentDate}</p><h1>{greeting}, {displayName}</h1><p>Here is the current picture of your hospital workspace.</p></div>
      </div>

      {dashboardQuery.isError ? (
        <ErrorState title="Unable to load department activity" message="The live dashboard summary could not be loaded." action={{ label: 'Try again', onClick: () => void dashboardQuery.refetch() }} />
      ) : dashboardQuery.isLoading ? (
        <DepartmentCardsSkeleton />
      ) : (
        <section className="dept-grid" aria-label="Department summary">
          {departments.map((dept) => <DepartmentCard key={dept.key} dept={dept} />)}
        </section>
      )}

      <div className="chart-grid">
        <section className="chart-card">
          <div className="section-heading">
            <div><p className="eyebrow">By department</p><h2>Patients — today vs yesterday</h2></div>
          </div>
          <div className="chart-body">
            {dashboardQuery.isLoading ? (
              <ChartSkeleton height={260} bars={6} />
            ) : dashboardQuery.isError ? (
              <ErrorState title="Unable to load patient activity" message="Try refreshing the live department summary." action={{ label: 'Try again', onClick: () => void dashboardQuery.refetch() }} />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
              <BarChart data={patientChartData} barCategoryGap="28%" barGap={4}>
                <CartesianGrid vertical={false} stroke="#e7f0ee" />
                <XAxis dataKey="department" tick={{ fontSize: 11, fill: '#78908d' }} axisLine={{ stroke: '#dde8e6' }} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#78908d' }} axisLine={false} tickLine={false} width={28} />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(13,107,102,.06)' }} />
                <Legend wrapperStyle={{ fontSize: 12, color: '#5e7774' }} iconType="circle" iconSize={8} />
                <Bar dataKey="Yesterday" fill="#c7e6e1" radius={[5, 5, 0, 0]} maxBarSize={26} />
                <Bar dataKey="Today" fill="#0d6b66" radius={[5, 5, 0, 0]} maxBarSize={26} />
              </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </section>

        <section className="chart-card">
          <div className="section-heading">
            <div><p className="eyebrow">₹ by department</p><h2>Collection — today vs yesterday</h2></div>
          </div>
          <div className="chart-body">
            {collectionQuery.isLoading ? (
              <ChartSkeleton height={260} bars={6} />
            ) : collectionQuery.isError ? (
              <ErrorState title="Unable to load collections" message="The billing summary could not be loaded." action={{ label: 'Try again', onClick: () => void collectionQuery.refetch() }} />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
              <BarChart data={collectionChartData} barCategoryGap="28%" barGap={4}>
                <CartesianGrid vertical={false} stroke="#e7f0ee" />
                <XAxis dataKey="department" tick={{ fontSize: 11, fill: '#78908d' }} axisLine={{ stroke: '#dde8e6' }} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#78908d' }} axisLine={false} tickLine={false} width={40} tickFormatter={(value: number) => `₹${value}`} />
                <Tooltip content={<ChartTooltip prefix="₹" />} cursor={{ fill: 'rgba(55,119,183,.06)' }} />
                <Legend wrapperStyle={{ fontSize: 12, color: '#5e7774' }} iconType="circle" iconSize={8} />
                <Bar dataKey="Yesterday" fill="#bcdcf3" radius={[5, 5, 0, 0]} maxBarSize={26} />
                <Bar dataKey="Today" fill="#3777b7" radius={[5, 5, 0, 0]} maxBarSize={26} />
              </BarChart>
              </ResponsiveContainer>
            )}
          </div>
          <p className="chart-footnote">Recorded receipts are updated automatically from every billing module.</p>
        </section>
      </div>

      <section className="chart-card">
        <div className="section-heading">
          <div><p className="eyebrow">Last {trendDays} days</p><h2>Patient registrations trend</h2></div>
          <div className="trend-range-picker">
            <label>Range
              <select value={trendDays} onChange={(event) => setTrendDays(Number(event.target.value) as TrendDays)}>
                {TREND_DAY_OPTIONS.map((days) => <option key={days} value={days}>{days} days</option>)}
              </select>
            </label>
            <span className="text-link" aria-hidden="true">{totalPatients.toLocaleString('en-IN')} total in directory</span>
          </div>
        </div>
        <div className="chart-body">
          {statsQuery.isLoading ? (
            <ChartSkeleton height={220} bars={trendDays} />
          ) : (
            <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={trendData} margin={{ top: 6, right: 12, left: 0, bottom: 4 }}>
              <defs>
                <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0d6b66" stopOpacity={0.28} />
                  <stop offset="100%" stopColor="#0d6b66" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} stroke="#e7f0ee" />
              <XAxis
                dataKey="day"
                tick={{ fontSize: 10.5, fill: '#78908d' }}
                axisLine={{ stroke: '#dde8e6' }}
                tickLine={false}
                interval={0}
                tickMargin={6}
                padding={{ left: 16, right: 16 }}
                tickFormatter={(value: string, index: number) => {
                  const step = trendDays === 30 ? 5 : trendDays === 14 ? 2 : 1;
                  return index % step === 0 || index === trendData.length - 1 ? value : '';
                }}
              />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#78908d' }} axisLine={false} tickLine={false} width={34} />
              <Tooltip content={<ChartTooltip />} cursor={{ stroke: '#0d6b66', strokeWidth: 1, strokeDasharray: '4 4' }} />
              <Area type="monotone" dataKey="Registrations" stroke="#0d6b66" strokeWidth={2.4} fill="url(#trendFill)" dot={trendDays > 14 ? false : { r: 3, fill: '#0d6b66', strokeWidth: 0 }} activeDot={{ r: 4 }} />
            </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </section>

      <div className="dashboard-grid">
        <section className="content-card">
          <div className="section-heading"><div><p className="eyebrow">Registration details</p><h2>Recently registered</h2></div><Link className="text-link" to="/patients">View all →</Link></div>
          {patientsQuery.isLoading ? <LoadingState rows={5} columns={5} label="Loading recent patients" /> : null}
          {patientsQuery.isError ? <ErrorState title="Unable to load activity" message="Check that the API and MongoDB are running." /> : null}
          {patientsQuery.isSuccess && recentPatients.length === 0 ? <p className="quiet-message">New patient registrations will appear here.</p> : null}
          {recentPatients.length > 0 ? (
            <div className="table-scroll">
              <table>
                <thead><tr><th>Patient</th><th>Blood</th><th>Contact</th><th>Registered</th><th>Status</th></tr></thead>
                <tbody>
                  {recentPatients.map((patient) => (
                    <tr key={patient.id}>
                      <td>
                        <div className="patient-name">
                          <span>{patient.firstName.charAt(0)}{patient.lastName.charAt(0)}</span>
                          <div><strong>{patient.firstName} {patient.lastName}</strong><small>{patient.medicalRecordNumber}</small></div>
                        </div>
                      </td>
                      <td><span className="blood-badge">{patient.bloodGroup === 'unknown' ? '—' : patient.bloodGroup}</span></td>
                      <td><div className="contact-cell"><span>{patient.phone}</span><small>{patient.email ?? 'No email'}</small></div></td>
                      <td>{formatDateValue(patient.createdAt)}</td>
                      <td><span className={`status-badge ${patient.status}`}>{patient.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </section>

        <aside className="content-card quick-panel">
          <div className="section-heading"><div><p className="eyebrow">Snapshot</p><h2>Quick view</h2></div></div>
          <div className="quick-stat-row">
            <div><strong>{statsQuery.isLoading ? <Sk style={{ display: 'inline-block', width: 44, height: 15 }} /> : (stats?.active ?? 0).toLocaleString('en-IN')}</strong><small>Active patients</small></div>
            <div><strong>{statsQuery.isLoading ? <Sk style={{ display: 'inline-block', width: 44, height: 15 }} /> : (stats?.archived ?? 0).toLocaleString('en-IN')}</strong><small>Archived</small></div>
            <div><strong>{collectionQuery.isLoading ? <Sk style={{ display: 'inline-block', width: 64, height: 15 }} /> : currency.format(collectionToday?.totals.total ?? 0)}</strong><small>Collected today</small></div>
          </div>
          <Link to="/patients"><span>⌕</span><div><strong>Find a patient</strong><small>Search the directory</small></div><b>›</b></Link>
          <div className="foundation-note"><strong>Built for growth</strong><p>Add appointments, staff, billing, pharmacy, labs, wards, and authorization as independent modules.</p></div>
        </aside>
      </div>
    </div>
  );
};
